import React, { useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import { ManageAccounts } from '@mui/icons-material';
import { useTranslate } from '@refinedev/core';

interface BillingPortalButtonProps {
  customerId: string;
  onError?: (error: string) => void;
}

const BillingPortalButton: React.FC<BillingPortalButtonProps> = ({
  customerId,
  onError
}) => {
  const translate = useTranslate();
  const [loading, setLoading] = useState(false);

  const handlePortalAccess = async () => {
    if (!customerId) {
      onError?.(translate('subscription.billingPortal.missingCustomerId'));
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('http://localhost:3002/create-portal-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customerId,
          returnUrl: window.location.href,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.details || data.error || translate('subscription.billingPortal.billingSessionError'));
      }

      if (!data.url) {
        throw new Error(translate('subscription.billingPortal.noPortalUrl'));
      }

      window.location.href = data.url;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : translate('subscription.billingPortal.billingPortalError');
      onError?.(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      variant="contained"
      startIcon={loading ? <CircularProgress size={16} /> : <ManageAccounts />}
      onClick={handlePortalAccess}
      disabled={loading}
      size="large"
      sx={{
        bgcolor: 'success.main',
        '&:hover': {
          bgcolor: 'success.dark',
        }
      }}
    >
      {loading ? translate('subscription.billingPortal.loading') : translate('subscription.billingPortal.manageBilling')}
    </Button>
  );
};

export default BillingPortalButton;
